import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { Plus, Loader2, DollarSign, Heart, Building2, Save, X as XIcon } from 'lucide-react';

export default function SettingsTab({config, setConfig, api}) {
  const [saving, setSaving] = React.useState(false);
  const [local, setLocal] = React.useState(config);
  React.useEffect(() => { setLocal(config); }, [config]);

  const addItem = (key, template) => setLocal(p => ({ ...p, [key]: [...(p[key]||[]), { id: `${key}-${Date.now()}`, ...template }] }));
  const updateItem = (key, idx, field, value) => setLocal(p => ({ ...p, [key]: p[key].map((it, i) => i === idx ? { ...it, [field]: value } : it) }));
  const removeItem = (key, idx) => setLocal(p => ({ ...p, [key]: p[key].filter((_, i) => i !== idx) }));

  const save = async () => {
    setSaving(true);
    try {
      const cleanBands = (local.salary_bands || []).filter(b => (b.label || '').trim()).map(b => ({ ...b, min: parseFloat(b.min) || 0, max: parseFloat(b.max) || 0 }));
      const cleanBenefits = (local.benefits || []).filter(b => (b.label || '').trim());
      const cleanDivs = (local.divisions || []).filter(d => (d.label || '').trim());
      const { data } = await api.put('/staffing/config', { salary_bands: cleanBands, benefits: cleanBenefits, divisions: cleanDivs });
      setConfig({ salary_bands: data.salary_bands, benefits: data.benefits, divisions: data.divisions });
      toast.success('Settings saved');
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  return (
    <div className="mt-5 space-y-5" data-testid="staffing-settings-tab">
      <Card className="border-[#DED8EB] shadow-sm">
        <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm font-semibold text-[#2D1B4E] flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-[#6B3FA0]"/>Salary Bands
          </CardTitle>
          <Button size="sm" variant="outline" onClick={()=>addItem('salary_bands', { label: '', min: 0, max: 0 })} className="border-[#9B7ED8] text-[#6B3FA0] h-7 text-xs gap-1" data-testid="add-salary-band"><Plus className="h-3 w-3"/>Band</Button>
        </CardHeader>
        <CardContent className="space-y-2">
          {(local.salary_bands||[]).length === 0 && <p className="text-xs text-[#8E85A0] py-2 text-center">No bands yet. Add one above.</p>}
          {(local.salary_bands||[]).map((b, i) => (
            <div key={b.id || i} className="grid grid-cols-12 gap-2 items-center">
              <Input value={b.label||''} onChange={e=>updateItem('salary_bands', i, 'label', e.target.value)} placeholder="Band A – Entry" className="border-[#DED8EB] col-span-5 text-sm" data-testid={`band-label-${i}`}/>
              <div className="col-span-3 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[#8E85A0]">$</span>
                <Input type="number" step="0.5" value={b.min||''} onChange={e=>updateItem('salary_bands', i, 'min', e.target.value)} placeholder="15" className="border-[#DED8EB] pl-6 text-sm" data-testid={`band-min-${i}`}/>
              </div>
              <div className="col-span-3 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[#8E85A0]">$</span>
                <Input type="number" step="0.5" value={b.max||''} onChange={e=>updateItem('salary_bands', i, 'max', e.target.value)} placeholder="18" className="border-[#DED8EB] pl-6 text-sm" data-testid={`band-max-${i}`}/>
              </div>
              <Button variant="ghost" size="icon" onClick={()=>removeItem('salary_bands', i)} className="col-span-1 h-8 w-8 text-[#8E85A0] hover:text-[#D9534F]" data-testid={`remove-band-${i}`}><XIcon className="h-3.5 w-3.5"/></Button>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="border-[#DED8EB] shadow-sm">
        <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm font-semibold text-[#2D1B4E] flex items-center gap-2">
            <Heart className="h-4 w-4 text-[#6B3FA0]"/>Benefits Catalog
          </CardTitle>
          <Button size="sm" variant="outline" onClick={()=>addItem('benefits', { label: '' })} className="border-[#9B7ED8] text-[#6B3FA0] h-7 text-xs gap-1" data-testid="add-benefit"><Plus className="h-3 w-3"/>Benefit</Button>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-[11px] text-[#8E85A0]">These show as checkboxes inside the Job Description editor.</p>
          {(local.benefits||[]).length === 0 && <p className="text-xs text-[#8E85A0] py-2 text-center">No benefits yet.</p>}
          {(local.benefits||[]).map((b, i) => (
            <div key={b.id || i} className="grid grid-cols-12 gap-2 items-center">
              <Input value={b.label||''} onChange={e=>updateItem('benefits', i, 'label', e.target.value)} placeholder="Medical Insurance" className="border-[#DED8EB] col-span-11 text-sm" data-testid={`benefit-label-${i}`}/>
              <Button variant="ghost" size="icon" onClick={()=>removeItem('benefits', i)} className="col-span-1 h-8 w-8 text-[#8E85A0] hover:text-[#D9534F]" data-testid={`remove-benefit-${i}`}><XIcon className="h-3.5 w-3.5"/></Button>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="border-[#DED8EB] shadow-sm">
        <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm font-semibold text-[#2D1B4E] flex items-center gap-2">
            <Building2 className="h-4 w-4 text-[#6B3FA0]"/>Divisions
          </CardTitle>
          <Button size="sm" variant="outline" onClick={()=>addItem('divisions', { label: '' })} className="border-[#9B7ED8] text-[#6B3FA0] h-7 text-xs gap-1" data-testid="add-division"><Plus className="h-3 w-3"/>Division</Button>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-[11px] text-[#8E85A0]">Divisions appear in Position &amp; Job Description editors and drive Projections rollups.</p>
          {(local.divisions||[]).length === 0 && <p className="text-xs text-[#8E85A0] py-2 text-center">No divisions yet.</p>}
          {(local.divisions||[]).map((d, i) => (
            <div key={d.id || i} className="grid grid-cols-12 gap-2 items-center">
              <Input value={d.label||''} onChange={e=>updateItem('divisions', i, 'label', e.target.value)} placeholder="IDD Services" className="border-[#DED8EB] col-span-11 text-sm" data-testid={`division-label-${i}`}/>
              <Button variant="ghost" size="icon" onClick={()=>removeItem('divisions', i)} className="col-span-1 h-8 w-8 text-[#8E85A0] hover:text-[#D9534F]" data-testid={`remove-division-${i}`}><XIcon className="h-3.5 w-3.5"/></Button>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="flex items-center justify-end pt-2 border-t border-[#DED8EB]">
        <Button onClick={save} disabled={saving} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-2" data-testid="save-staffing-config">
          {saving ? <Loader2 className="h-4 w-4 animate-spin"/> : <Save className="h-4 w-4"/>}
          {saving ? 'Saving...' : 'Save All Settings'}
        </Button>
      </div>
    </div>
  );
}
